<template>
  <section class="bm-panel" data-testid="batches-panel">
    <header class="bm-panel__head">
      <h2 class="bm-panel__title">Batches</h2>
      <!--
        Panel lede: the first of the three "when money moves" levels. Always
        visible, because WHEN the charge happens is what makes editing legible
        as safe before it.

        The until-clause is BACK, and this time it is true. #2790 removed it
        because release charged nothing (`release_batch` ran with no
        `charge_fn`); #3184 restores it because release now charges the saved
        card on each order for real (`charge_fn=make_charge_fn()`).
      -->
      <p class="bm-panel__lede" data-testid="panel-charge-at-release">
        Orders collect into a batch and are sent together.
        <b>The saved card on each order is charged when the batch is released</b> &mdash; until
        then, everything in an open or holding batch can still be fixed.
      </p>
    </header>

    <BatchingRulesPanel v-if="!rulesLocked" :rules="rules" @update:rules="onRulesChange" />

    <!--
      A rejected SETTINGS save reports here, deliberately NOT through
      `loadError`. `loadError` lives in the v-else-if chain below and REPLACES
      the entire batch list -- blanking every batch a distributor was reading
      because one toggle failed to save would be a worse outcome than the
      silent-keep this fixes. The list stays; the failed control speaks.
    -->
    <div v-if="rulesError" class="bm-error" data-testid="batch-rules-error">
      <i class="material-icons" aria-hidden="true">error_outline</i>
      <span>{{ rulesError }}</span>
    </div>

    <div class="bm-filters">
      <div class="bm-filters__chips" data-testid="batch-filters">
        <button
          v-for="f in filters"
          :key="f.value"
          type="button"
          class="bm-filter"
          :class="{
            'bm-filter--on': filter === f.value,
            'bm-filter--warn': f.value === 'needs' && counts.needs > 0 && filter !== f.value,
          }"
          :data-testid="`filter-${f.value}`"
          :aria-pressed="String(filter === f.value)"
          @click="filter = f.value"
        >
          {{ f.label }}
          <span class="bm-filter__count">{{ counts[f.value] }}</span>
        </button>
      </div>
      <div class="bm-search">
        <i class="material-icons bm-search__icon" aria-hidden="true">search</i>
        <input
          v-model="query"
          class="bm-search__input"
          data-testid="batch-search"
          type="search"
          placeholder="Batch number or shopper"
          aria-label="Search batches by number or shopper"
        />
      </div>
    </div>

    <div v-if="loading" class="bm-loading" data-testid="batches-loading">
      <span class="bm-loading__spinner" />
      <span>Loading batches&hellip;</span>
    </div>

    <div v-else-if="loadError" class="bm-error" data-testid="batches-error">
      <i class="material-icons" aria-hidden="true">error_outline</i>
      <span>{{ loadError }}</span>
      <button type="button" class="bm-btn" data-testid="batches-retry-load" @click="load">Try again</button>
    </div>

    <template v-else>
      <!-- Brand-new store: no batches at all. -->
      <BatchEmptyState
        v-if="batches.length === 0"
        testid="empty-no-batches"
        art="empty_city.svg"
        title="No batches yet"
        body="You don't create batches -- the store does. The first order a shopper places opens one, and it collects orders until the window closes or the threshold is met."
      >
        <template #action>
          <button type="button" class="bm-btn" data-testid="copy-store-link" @click="$emit('copy-link')">
            <i class="material-icons bm-btn__icon" aria-hidden="true">link</i>
            Copy the store link
          </button>
        </template>
      </BatchEmptyState>

      <!-- A filter or query that matched nothing. -->
      <BatchEmptyState
        v-else-if="visibleBatches.length === 0"
        testid="empty-no-results"
        art="search.svg"
        title="No batches match"
        body="Nothing here matches that filter and search. Clear them to see the whole history."
      >
        <template #action>
          <button type="button" class="bm-btn" data-testid="clear-filters" @click="clearFilters">
            Clear the filter
          </button>
        </template>
      </BatchEmptyState>

      <template v-else>
        <BatchRow
          v-for="b in visibleBatches"
          :key="b.id"
          :batch="b"
          :rules="rules"
          :expanded="expandedId === b.id"
          :closing="closingBatchIds.includes(b.id)"
          :progress="releaseProgress[b.id] || null"
          :preflight="preflightById[b.id] || null"
          @toggle="toggle"
          @release="openRelease"
          @close-batch="closeBatch"
          :abandoning="abandoningBatchIds.includes(b.id)"
          :busy-item-ids="busyItemIds"
          @retry="retry"
          @retry-open="retryOpenRegistration"
          @retry-item="retryStrandedItem"
          @rearm-item="rearmStrandedItem"
          @abandon="abandonBatch"
          @edit-order="openEditor"
          @cancel-order="quickCancel"
          :can-move="moveDestinationsFor(b).length > 0"
          @move-order="(o) => openMove(b, o)"
          @copy-link="$emit('copy-link')"
          @details-updated="replaceBatch"
        />

        <div v-if="loadingMore" class="bm-more" data-testid="batches-loading-more">
          <span class="bm-loading__spinner" />
          <span>Loading older batches&hellip;</span>
        </div>
        <button
          v-else-if="hasMore"
          type="button"
          class="bm-btn bm-more__btn"
          data-testid="batches-load-more"
          @click="loadMore"
        >
          Load older batches
        </button>
        <p v-else class="bm-end" data-testid="batches-end">That is every batch for this store.</p>
      </template>
    </template>

    <ReleaseReview
      v-if="releaseTarget"
      v-model="releaseOpen"
      :batch="releaseTarget"
      :rules="rules"
      :busy="releasing"
      @confirm="confirmRelease"
    />

    <ReleaseResult
      v-if="resultBatch"
      v-model="resultOpen"
      :batch="resultBatch"
      :outcome="resultOutcome"
      :outcome-error="resultOutcomeError"
      :next-batch-id="resultNextBatchId"
      @open-next="goToNextBatch"
    />

    <OrderEditor
      v-if="editorOrder"
      v-model="editorOpen"
      :order="editorOrder"
      :batch="editorBatch"
      @save="saveOrder"
      @cancel-order="confirmCancelOrder"
    />

    <MoveOrdersDialog
      v-if="moveSource"
      :key="moveKey"
      v-model="moveOpen"
      :source="moveSource"
      :initial-order="moveOrder"
      :destinations="moveDestinationsFor(moveSource)"
      :busy="moving"
      :error="moveError"
      @confirm="confirmMove"
    />
  </section>
</template>

<script>
/**
 * The Batches tab of the store editor.
 *
 * Design: design-context/latest/2026-08-20-batch-management/
 *   design/Locked Decisions.md + adr/0066-batch-management.md
 *
 * This component owns the state that spans the surface -- the list, the
 * filter, which batch is expanded, and which overlay is open -- and owns the
 * ONE irreversible call (release). Child components emit intent; they never
 * call the API themselves, so there is exactly one implementation of the
 * action that sends the batch to the decorator.
 *
 * THE API IS LIVE (api/src/endpoints/batch_management.py, #2767). This panel
 * reads real data; every failure, including a 404, surfaces as an error state.
 *
 * The design fixtures are still here, but they are now opt-in via the
 * `demoFixtures` prop and are never selected by inspecting a status code --
 * see the note above PAGE_SIZE for why that distinction is load-bearing. Do
 * not set that prop on any surface a distributor can reach.
 */
import BatchRow from "./BatchRow.vue";
import BatchingRulesPanel from "./BatchingRulesPanel.vue";
import BatchEmptyState from "./BatchEmptyState.vue";
import ReleaseReview from "./ReleaseReview.vue";
import ReleaseResult from "./ReleaseResult.vue";
import OrderEditor from "./OrderEditor.vue";
import MoveOrdersDialog from "./MoveOrdersDialog.vue";
import { isEditable, needsYou } from "./batchStatus";
import {
  listBatches,
  getStoreBatchRules,
  updateStoreBatchRules,
  releaseBatch,
  closeBatchToNewOrders,
  retryRelease,
  retryOpen,
  getReleaseOutcome,
  getBatchPreflight,
  retryReleaseItem,
  rearmReleaseItem,
  abandonRelease,
  updateOrderLines,
  cancelOrder,
  moveOrdersToBatch,
  makeFixtureBatches,
  getReleaseProgress,
  DEFAULT_RULES,
} from "@/services/batchManagementService";

const PAGE_SIZE = 8;

/*
  How often a releasing batch is re-read. 3s, following the
  DecorationLibraryList preview poll (2.5s) closely enough to be the same
  idiom rather than a second one.

  The endpoint is explicitly NOT rate limited (`ui_release_progress` says so
  in its docstring: "a limit on a poll is a limit on the operator's ability
  to watch the thing they were told to watch"), and it is one indexed
  GROUP BY on a partial index -- so the cost of this interval is understood
  rather than assumed.
*/
const RELEASE_POLL_INTERVAL_MS = 3000;

/**
 * Does this rejected close mean the batch is ALREADY closed?
 *
 * Two distinct 409s from `ui_close_batch` both mean the work is done:
 *
 *   1. `batch N is not open (state 'holding'); only an open batch can be
 *      closed to new orders` -- somebody (often an earlier click of the
 *      same burst) already closed it. The same sentence carries any other
 *      non-open state, `released` included; all of them mean the batch has
 *      stopped collecting orders.
 *   2. `batch N was moved out of 'open' by another actor before this close
 *      could be applied` -- we lost the compare-and-swap. The batch IS
 *      closed; it just was not us who closed it.
 *
 * Both are the user's intended end state, so neither is a failure.
 *
 * WHAT IS MATCHED, AND WHY IT IS THE STABLE PART. Status 409 plus one of
 * two short substrings -- `is not open` and `moved out of`. Never the whole
 * sentence: the prose around these fragments is explanatory and gets
 * reworded, and a full-sentence match would silently stop working the next
 * time someone improves the wording, restoring the exact bug this fixes.
 *
 * The fragments were chosen because they carry the MEANING rather than the
 * phrasing. `is not open` is the negation of the one state that permits a
 * close, and `moved out of` is the verb phrase of the CAS-loss branch,
 * which has no other message.
 *
 * MATCH THE NEGATION, NOT THE STATES. The first version of this enumerated
 * `state 'holding'`, on the reasoning that an already-RELEASED batch is a
 * different situation the distributor should hear about. That was wrong on
 * both counts. `released` is equally "this batch is not collecting orders"
 * -- the user's intent is satisfied either way -- and enumerating states
 * means every state added later silently falls through to a red banner
 * over a batch that is, in fact, closed. That is the same false failure
 * this function exists to stop, one state over.
 *
 * Verified safe to widen: `is not open` is emitted from exactly ONE place
 * in the API (`ui_close_batch`, batch_management.py:1719). The only other
 * `state != "open"` check, at :456, returns None and emits no message, and
 * the string in batch_release.py:30 is prose in a docstring. So this cannot
 * capture an unrelated 409.
 *
 * This is string-matching an English message and that is a weakness, not a
 * design. The right long-term fix is a machine-readable code on the error
 * envelope (e.g. `{"error": ..., "code": "batch_not_open"}`); see the PR
 * for the follow-up. Backend changes were out of scope here.
 */
function isAlreadyClosed(err) {
  const res = err && err.response;
  if (!res || res.status !== 409) return false;
  const message = String((res.data && res.data.error) || "");
  return message.includes("is not open") || message.includes("moved out of");
}

// A 404 used to mean "the batch-management API has not landed yet", and
// rendering design fixtures was the right response to it. The API landed
// (#2767), and the SAME 404 now means something entirely different -- the API
// returns it DELIBERATELY for cross-tenant access, so that a foreign id is
// indistinguishable from a non-existent one.
//
// Inferring "show fixtures" from that status code would put fabricated batches,
// carrying plausible money, on the screen of a distributor looking at a store
// they do not own -- or on every screen if a deploy went out broken. Invented
// money is worse than an error message, and it is worse precisely because it
// looks fine.
//
// So the fallback is now an explicit opt-in (`demoFixtures`) chosen by whoever
// mounts the panel. Every status code, 404 included, fails loudly.

export default {
  name: "BatchesPanel",
  components: {
    BatchRow,
    BatchingRulesPanel,
    BatchEmptyState,
    ReleaseReview,
    ReleaseResult,
    OrderEditor,
    MoveOrdersDialog,
  },
  props: {
    storeId: { type: [String, Number], required: true },
    // Render the design fixtures instead of live data when the API is
    // unavailable. Opt-in ONLY -- see the note above `PAGE_SIZE`. Never set
    // this on a surface a distributor can reach.
    demoFixtures: { type: Boolean, default: false },
    // The batching RULES are a store setting, and an ARCHIVED store refuses
    // writes to its settings (the server answers the rules PATCH with the
    // archived 404). The editor's restricted Batches-only mode for an archived
    // store sets this, so the rules are not offered; every batch action stays.
    rulesLocked: { type: Boolean, default: false },
  },
  emits: ["copy-link"],
  data() {
    return {
      batches: [],
      rules: { ...DEFAULT_RULES },
      loading: true,
      loadingMore: false,
      loadError: "",
      rulesError: "",
      hasMore: false,
      page: 1,
      filter: "all",
      query: "",
      expandedId: null,

      releaseOpen: false,
      releaseTarget: null,
      releasing: false,

      // The ids of batches whose close is in flight. Per-batch rather than
      // a boolean: a panel can show several open batches, each with its own
      // close control, and one boolean would disable all of them the moment
      // any one was pressed.
      closingBatchIds: [],

      // Live drain progress, keyed by batch id. Populated ONLY by the poll;
      // a batch with no entry has not been polled yet, which the row renders
      // as "Starting the release..." rather than as zero progress.
      releaseProgress: {},
      // The design preflight (card #643), keyed by batch id. Loaded when a
      // row is opened and re-read after anything that can change it; a batch
      // with no entry has none loaded, which the row renders as no list.
      preflightById: {},
      // Latest read ticket per batch id (see `loadPreflight`). Plain object,
      // never rendered, so it is not reactive state.
      preflightTickets: {},
      // setInterval handle for the progress poll. Non-null only while at
      // least one batch is `releasing`; cleared when none is, and on unmount
      // so the poll never outlives the component.
      releasePollTimer: null,
      // In-flight guard, the DecorationLibraryList one: the fixed interval is
      // decoupled from the async fetch, so a response slower than 3s could
      // overlap the next tick and two out-of-order resolves could write a
      // stale progress object over a fresher one.
      releasePollInFlight: false,
      // Per-batch and per-item rather than booleans, for the same reason
      // `closingBatchIds` is: the panel shows several batches at once, and
      // one flag would disable every control the moment any one was pressed.
      abandoningBatchIds: [],
      busyItemIds: [],

      resultOpen: false,
      // The id of the batch the result screen is about. The screen reads the
      // LIVE batch out of `batches` (see `resultBatch`), never a snapshot: the
      // 202 response is a `releasing` batch whose outcome is not known yet,
      // and a snapshot froze that into a permanent "all sent" verdict (#424).
      resultBatchId: null,
      // The batch as it was when release was confirmed. Only a fallback for a
      // batch that has dropped out of the loaded page.
      resultSnapshot: null,
      // What the release actually did, per order (getReleaseOutcome). null
      // until the batch has left `releasing`.
      resultOutcome: null,
      resultOutcomeError: false,
      resultOutcomeInFlight: false,
      resultNextBatchId: "",

      editorOpen: false,
      editorOrder: null,
      editorBatch: null,

      // Move orders to another batch of this store (AH closeout tooling).
      // `moveKey` remounts the dialog per opening so its picks start fresh.
      moveOpen: false,
      moveSource: null,
      moveOrder: null,
      moveKey: 0,
      moving: false,
      moveError: "",

      filters: [
        { value: "all", label: "All" },
        { value: "needs", label: "Needs you" },
        { value: "open", label: "Open" },
        { value: "released", label: "Released" },
        { value: "failed", label: "Failed" },
      ],
    };
  },
  computed: {
    resultBatch() {
      if (this.resultBatchId == null) return null;
      return (
        this.batches.find((b) => b.id === this.resultBatchId) ||
        this.resultSnapshot
      );
    },
    counts() {
      const by = (fn) => this.batches.filter(fn).length;
      return {
        all: this.batches.length,
        // "Needs you" is Holding plus Failed: the states only you can clear.
        needs: by((b) => needsYou(b.status)),
        open: by((b) => b.status === "open"),
        released: by((b) => b.status === "released"),
        failed: by((b) => b.status === "failed"),
      };
    },
    visibleBatches() {
      const q = this.query.trim().toLowerCase();
      return this.batches.filter((b) => {
        if (this.filter === "needs" && !needsYou(b.status)) return false;
        if (["open", "released", "failed"].includes(this.filter) && b.status !== this.filter) {
          return false;
        }
        if (!q) return true;
        if (String(b.id).toLowerCase().includes(q)) return true;
        return (b.orders || []).some((o) =>
          String(o.shopper || "").toLowerCase().includes(q)
        );
      });
    },
  },
  created() {
    this.load();
  },
  unmounted() {
    // The poll must not outlive the component. Without this the interval
    // keeps firing against an unmounted instance after the operator
    // navigates away -- a request every 3 seconds, forever.
    this.stopReleaseProgressPoll();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const [res, rules] = await Promise.all([
          listBatches(this.storeId, { page: 1, perPage: PAGE_SIZE }),
          this.loadRules(),
        ]);
        this.batches = res.batches;
        this.hasMore = res.hasMore;
        this.rules = rules;
        this.page = 1;
        // A load can LAND on a releasing batch -- that is exactly what a
        // refresh mid-release does -- so the poll starts from the data, not
        // from having pressed the button in this session.
        this.syncReleaseProgressPoll();
        this.refreshExpandedPreflight();
      } catch (err) {
        if (this.demoFixtures) {
          // Explicitly requested by the caller -- a design-review surface, not
          // anything a distributor reaches. Say so in the console rather than
          // pretending this is live data.
          // eslint-disable-next-line no-console
          console.info(
            "[batches] demoFixtures is set; rendering design fixtures, NOT live data."
          );
          this.batches = makeFixtureBatches();
          this.hasMore = false;
          this.rules = { ...DEFAULT_RULES };
        } else {
          this.loadError = "Could not load batches for this store.";
        }
      } finally {
        this.loading = false;
      }
    },

    async loadRules() {
      try {
        return await getStoreBatchRules(this.storeId);
      } catch (err) {
        // Swallowing this would show the PLATFORM defaults as though they were
        // this store's configured rules -- a distributor who raised their
        // free-freight threshold would be shown the default on the screen
        // before an irreversible charge. Only the opt-in demo surface may.
        if (this.demoFixtures) return { ...DEFAULT_RULES };
        throw err;
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      try {
        const next = this.page + 1;
        const res = await listBatches(this.storeId, { page: next, perPage: PAGE_SIZE });
        this.batches = this.batches.concat(res.batches);
        this.hasMore = res.hasMore;
        this.page = next;
      } catch (err) {
        this.loadError = "Could not load older batches.";
      } finally {
        this.loadingMore = false;
      }
    },

    toggle(id) {
      this.expandedId = this.expandedId === id ? null : id;
      // Fetch the stranded detail when a BLOCKED row is opened. Guarded
      // inside `loadStrandedItems`, so an ordinary batch costs no request.
      if (this.expandedId === id) {
        const batch = this.batches.find((b) => b.id === id);
        if (batch) {
          this.loadStrandedItems(batch);
          this.loadPreflight(batch);
        }
      }
    },

    clearFilters() {
      this.filter = "all";
      this.query = "";
    },

    async onRulesChange(next) {
      const previous = this.rules;
      // Optimistic: the prose reads wrong if the value it shows lags a click.
      this.rules = next;
      this.rulesError = "";
      try {
        await updateStoreBatchRules(this.storeId, next);
      } catch (err) {
        // Roll the optimistic value back and SAY SO. Keeping it on a failed
        // save shows a distributor a setting the server rejected -- they walk
        // away believing auto-release is off when it is still on.
        if (this.demoFixtures) return;
        this.rules = previous;
        this.rulesError = "Could not save that batching setting. It is unchanged.";
      }
    },

    openRelease(batch) {
      this.releaseTarget = batch;
      this.releaseOpen = true;
    },

    async confirmRelease() {
      if (this.releasing) return;
      this.releasing = true;
      try {
        const res = await releaseBatch(this.releaseTarget);
        this.replaceBatch(res.batch);
        this.releaseOpen = false;
        // 202 + `releasing`: the work is now happening somewhere else, so
        // start watching it.
        this.syncReleaseProgressPoll();
        // The result screen opens on the batch's LIVE state. Right after the
        // 202 the batch is `releasing` and nothing is known, so it shows
        // progress; the verdict (sent / held back / not sent) is read from
        // the server once the batch leaves `releasing`.
        this.resultBatchId = res.batch.id;
        this.resultSnapshot = res.batch;
        this.resultOutcome = null;
        this.resultOutcomeError = false;
        this.resultNextBatchId = res.nextBatchId || "";
        this.resultOpen = true;
        // Not awaited: it must not hold up the result screen (the release
        // poll is already running), and the ticket guard in `loadPreflight`
        // keeps a late answer from overwriting a newer one.
        this.loadPreflight(res.batch);
        await this.refreshResultOutcome();
      } catch (err) {
        // DO NOT claim the batch was not sent. This catch takes ANY error,
        // and the one that matters most is a timeout: releasing a large
        // batch can outlive the 120s ALB timeout, by which point cards may
        // already have been charged and orders already submitted. The old
        // copy ("The release did not go through. The batch was not sent.")
        // stated non-delivery as fact on a request whose outcome we cannot
        // see -- and it is wrong in the worst direction, because it invites
        // a retry of an operation that already happened.
        //
        // Unknown is an honest state. Send the operator to look at the real
        // state before they act, rather than telling them what it is.
        this.loadError =
          "We lost contact with the server during the release, so we do not know " +
          "whether it went through. The batch may have been sent. Refresh and check " +
          "this batch's status before retrying.";
        this.releaseOpen = false;
        // The outcome is unknown, so the old report may be wrong either way.
        const target = this.releaseTarget;
        const live = target && this.batches.find((b) => b.id === target.id);
        if (live) await this.loadPreflight(live);
      } finally {
        this.releasing = false;
      }
    },

    // ----- release progress poll -----------------------------------------
    //
    // Release is no longer synchronous (unit L: 202 + `releasing`), so the
    // panel has to ASK how far along a batch is. The shape below is the
    // DecorationLibraryList preview poll, deliberately: same in-flight
    // guard, same idempotent sync/start/stop trio, same unmount cleanup.
    // Copying a working idiom beats inventing a second one that has to be
    // reviewed from scratch.

    /**
     * Every batch the server currently says is draining.
     *
     * SERVER STATE, not a record of what this panel released. A batch that
     * was already releasing when the page loaded is polled too -- which is
     * the whole point, because that is what a refresh mid-release produces.
     */
    releasingBatches() {
      return this.batches.filter((b) => b.status === "releasing");
    },

    /**
     * Start the poll if anything is draining, stop it otherwise.
     *
     * Idempotent, so it is safe to call after every load, every release and
     * every poll -- which is what keeps the timer's existence a function of
     * the data rather than a thing to remember to turn off.
     */
    async syncReleaseProgressPoll() {
      if (this.releasingBatches().length) {
        // Poll IMMEDIATELY as well as starting the timer. Waiting a full
        // interval for the first number leaves a bar empty for 3 seconds
        // after the operator presses release, which reads as a dead button.
        this.startReleaseProgressPoll();
        await this.pollReleaseProgress();
      } else {
        this.stopReleaseProgressPoll();
      }
    },

    startReleaseProgressPoll() {
      if (this.releasePollTimer != null) return; // already polling
      this.releasePollTimer = setInterval(() => {
        this.pollReleaseProgress();
      }, RELEASE_POLL_INTERVAL_MS);
    },

    stopReleaseProgressPoll() {
      if (this.releasePollTimer != null) {
        clearInterval(this.releasePollTimer);
        this.releasePollTimer = null;
      }
    },

    /**
     * One tick: re-read every releasing batch and update its progress.
     *
     * A failed tick is SWALLOWED, not surfaced. This is a background read
     * whose only job is to keep a number fresh; painting the panel's error
     * banner over a transient poll failure would replace a working screen
     * with an error on a release that is proceeding fine. The next tick
     * retries, and a genuinely broken server is already visible through
     * every other call this panel makes.
     */
    async pollReleaseProgress() {
      if (this.releasePollInFlight) return;
      const targets = this.releasingBatches();
      if (!targets.length) return;

      this.releasePollInFlight = true;
      try {
        const results = await Promise.all(
          targets.map((b) =>
            getReleaseProgress(b).then(
              (p) => ({ id: b.id, progress: p }),
              // Per-batch, so one failing batch does not blank the others.
              () => null
            )
          )
        );
        const next = { ...this.releaseProgress };
        for (const r of results) {
          if (r) next[r.id] = r.progress;
        }
        this.releaseProgress = next;

        // The drain moved the batch out of `releasing`. Reload so the row
        // shows its real terminal state -- released, or release_blocked
        // with stranded orders -- rather than a progress bar on a batch
        // that has finished.
        const finished = results.some(
          (r) => r && r.progress.state && r.progress.state !== "releasing"
        );
        if (finished) {
          await this.load();
          await this.refreshResultOutcome();
        }
      } finally {
        this.releasePollInFlight = false;
      }
      // Re-evaluate: stop once nothing is draining.
      if (!this.releasingBatches().length) this.stopReleaseProgressPoll();
    },

    /**
     * Read what the release did, once the batch has left `releasing`.
     *
     * No-op while the batch is still draining (no verdict to read), when no
     * result screen is about a batch, or while a read is in flight. A failed
     * read is surfaced as UNKNOWN on the result screen rather than swallowed:
     * the alternative is the false "all sent" this exists to remove.
     */
    async refreshResultOutcome() {
      const batch = this.resultBatch;
      if (!batch || batch.status === "releasing") return;
      if (this.resultOutcomeInFlight) return;
      this.resultOutcomeInFlight = true;
      try {
        this.resultOutcome = await getReleaseOutcome(batch);
        this.resultOutcomeError = false;
      } catch (err) {
        this.resultOutcome = null;
        this.resultOutcomeError = true;
      } finally {
        this.resultOutcomeInFlight = false;
      }
    },

    async closeBatch(batch) {
      // PRODUCTION INCIDENT, 2026-09-08 (AdventHealth, batch B-4). Five
      // POSTs landed in the same second -- `409, 200, 409, 409, 409`. The
      // server behaved correctly: `ui_close_batch` does a state-conditional
      // compare-and-swap and 409s every loser. But this panel only ever
      // sees the LAST response, so four losers painted "Could not close
      // that batch" over a close that had already succeeded.
      //
      // The guard mirrors `confirmRelease`'s, but keyed on the target's id
      // rather than a boolean. The set, not a single id, because the guard
      // has to agree with what the screen shows: BatchRow only disables the
      // control of the batch that is closing, so a second open batch LOOKS
      // clickable and must therefore BE clickable. A single-slot flag would
      // silently swallow that click and leave the user pressing a live
      // button that does nothing -- trading this bug for a quieter one.
      //
      // `BatchRow.onCloseClick` adds a synchronous debounce in front of
      // this for the double-fire that beats the flag's re-render.
      if (this.closingBatchIds.includes(batch.id)) return;
      this.closingBatchIds = this.closingBatchIds.concat(batch.id);
      try {
        this.replaceBatch(await closeBatchToNewOrders(batch));
        // Closing is TWO server-side writes: this batch moves to `holding`,
        // and a SUCCESSOR open batch is created in the same transaction so
        // the store always has somewhere to put an incoming order
        // (batch_lifecycle.close_batch_to_new_orders). `replaceBatch` only
        // maps over batches already loaded, so the successor -- which by
        // definition is not one of them -- stayed invisible until a reload.
        // The distributor saw their open batch go to Holding and no open
        // batch anywhere, which reads as "my store stopped taking orders".
        //
        // A full reload rather than a narrower merge because the response
        // CANNOT tell us the successor: the route returns
        // `{"batch": <the closed batch>}` and nothing more
        // (`_fresh_batch_envelope`). Its id is unknowable without refetching.
        //
        // The cost is real and accepted: `load()` resets to page 1, so a
        // distributor who had paged back through older batches returns to the
        // newest page. Showing the wrong thing (a store with no open batch)
        // is worse than losing scroll position. If that trade stops being
        // worth it, the fix is a successor field on the close response, not a
        // cleverer merge here.
        await this.load();
      } catch (err) {
        // A close that had ALREADY happened is the outcome the user asked
        // for, not a failure. Refresh so the screen shows the real state
        // and say nothing -- the red banner was the bug.
        if (isAlreadyClosed(err)) {
          await this.load();
          return;
        }
        this.loadError = "Could not close that batch to new orders.";
      } finally {
        this.closingBatchIds = this.closingBatchIds.filter((id) => id !== batch.id);
      }
    },

    async retry(batch) {
      try {
        const updated = await retryRelease(batch);
        this.replaceBatch(updated);
        this.loadPreflight(updated);
      } catch (err) {
        // Same false claim, same reason as `confirmRelease` -- retry is NOT a
        // lighter operation. `ui_retry_release` calls `release_batch` with
        // `charge_fn=make_charge_fn()`, on the same rate-limit bucket, and
        // its own docstring calls it "the same irreversible path". So it can
        // outlive the same 120s timeout with cards already charged, and
        // "the batch was not sent" is exactly as false here.
        //
        // Worse here, if anything: the operator is already recovering from a
        // failure, and telling them nothing was sent is what invites the next
        // retry of an operation that may have already completed.
        this.loadError =
          "We lost contact with the server during the retry, so we do not know " +
          "whether it went through. The batch may have been sent. Refresh and check " +
          "this batch's status before retrying again.";
      }
    },

    /**
     * Restart back-office registration after the automatic retries stopped.
     *
     * The response is the re-read batch either way, so a SUCCESSFUL retry
     * clears the banner and a failed one comes back still flagged with a
     * higher attempt count -- the operator sees which without a reload.
     *
     * Its own handler rather than a branch inside `retry`: they call
     * different endpoints, and a batch can need both.
     *
     * ITS ERROR COPY IS SOUND, unlike `retry`'s was (checked 2026-09-15).
     * "Could not register" is a claim about REGISTRATION, and this path
     * never charges or submits: `ui_retry_open` calls `retry_open_now`, and
     * release refuses outright without a `deconetwork_batch_id`. So no money
     * can have moved and no order can have shipped when this reports.
     *
     * It does still overstate slightly on a timeout -- registration may have
     * succeeded -- but that write is to a partner's registration surface,
     * not to a card, and `ui_retry_open` 409s an already-registered batch
     * rather than double-registering. Left as is deliberately; if it is ever
     * tightened, it belongs with the other unknown-outcome messages.
     */
    /**
     * Load the per-item detail for a blocked batch and attach it to the row.
     *
     * Fetched on EXPAND rather than with the list: it is only meaningful for
     * a blocked batch, and joining it into every page of every batch list
     * would make every row pay for a query almost none of them need.
     *
     * A failure here is deliberately NOT surfaced as a page error. The row's
     * blocked banner and its abandon control render from `status` alone and
     * are already correct; replacing a working screen with an error because
     * a secondary fetch failed would take away the operator's only way out
     * of the state this screen exists to resolve.
     */
    async loadStrandedItems(batch) {
      // A blocked batch, or a RELEASED one still owing refunds (option A):
      // abandon closes the batch, and the orders owed a refund must stay in
      // view until they are refunded. `refundsOwed` is served, so a released
      // batch owing nothing -- every batch before this change -- costs no
      // request and shows nothing.
      const owesRefunds =
        batch.status === "released" && batch.refundsOwed > 0;
      if (batch.status !== "release_blocked" && !owesRefunds) return;
      try {
        // The outcome, not just the items: `deliveredOrderIds` is what lets
        // the row scope its copy when part of the batch WAS sent (#424).
        const { items, deliveredOrderIds } = await getReleaseOutcome(batch);
        this.replaceBatch({ ...batch, strandedItems: items, deliveredOrderIds });
      } catch (err) {
        // Intentionally silent -- see above.
      }
    },

    /**
     * Load the design preflight for a batch and keep it for its row.
     *
     * Read-only, and no external call on the server, so it is safe to fetch
     * whenever a row is opened. A RELEASED batch has nothing left to hold
     * back, so it costs no request and any old report is dropped.
     *
     * A failed read is deliberately silent, like `loadStrandedItems`: the
     * row's own controls are already correct without it, and an error banner
     * over a working screen for a secondary read takes away the operator's
     * way out. The previous report is dropped on failure rather than kept: a
     * stale "needs a fix" list is a claim the server no longer makes.
     */
    async loadPreflight(batch) {
      if (!batch) return;
      // Per-batch ordering guard: every call takes a ticket, and only the
      // LATEST ticket may write. Overlapping reads (row open, Release, retry,
      // reload) resolve in any order, and a late OLD answer must neither
      // restore a "needs a fix" list on a batch that has moved on nor drop a
      // newer good report on its own failure. A released batch takes a
      // ticket too, so an in-flight read for it is discarded on arrival.
      const ticket = (this.preflightTickets[batch.id] || 0) + 1;
      this.preflightTickets[batch.id] = ticket;
      const current = () => this.preflightTickets[batch.id] === ticket;
      if (batch.status === "released") {
        if (this.preflightById[batch.id]) this.dropPreflight(batch.id);
        return;
      }
      try {
        const report = await getBatchPreflight(batch);
        if (!current()) return;
        this.preflightById = { ...this.preflightById, [batch.id]: report };
      } catch (err) {
        if (current()) this.dropPreflight(batch.id);
      }
    },

    dropPreflight(id) {
      const next = { ...this.preflightById };
      delete next[id];
      this.preflightById = next;
    },

    /** Re-read the open row's report after a reload changed the batches. */
    refreshExpandedPreflight() {
      const batch = this.batches.find((b) => b.id === this.expandedId);
      if (batch) this.loadPreflight(batch);
    },

    /**
     * Re-arm ONE stranded item, then re-read the batch's items.
     *
     * Re-reading rather than patching the row locally: the retry clears the
     * counter server-side and the drain may resolve the item moments later,
     * so a local edit would show a state the server has already left.
     */
    async retryStrandedItem(item) {
      if (this.busyItemIds.includes(item.id)) return;
      this.busyItemIds = [...this.busyItemIds, item.id];
      try {
        await retryReleaseItem(item.id);
        const batch = this.batches.find((b) => b.rawId === item.batchId)
          || this.batches.find((b) =>
            (b.strandedItems || []).some((i) => i.id === item.id));
        if (batch) {
          await this.loadStrandedItems(batch);
          await this.loadPreflight(batch);
        }
      } catch (err) {
        this.loadError = "Could not retry that order. Try again in a moment.";
      } finally {
        this.busyItemIds = this.busyItemIds.filter((id) => id !== item.id);
      }
    },

    /**
     * Revive ONE exhausted item whose cause has been repaired.
     *
     * DISTINCT FROM `retryStrandedItem`, which nudges an item the drain will
     * still pick up. This one acts on a TERMINAL item -- the 2026-09-25 B-31
     * shape, where all six attempts were spent against a back-office batch id
     * that 404'd and the screen's only remaining control closed the batch over
     * the shopper's money.
     *
     * Re-reads the batch's items rather than patching the row locally, for the
     * same reason the retry handler does: the server decides which state the
     * item returns to (`charged` when money moved, `pending` when it did not),
     * and the drain may resolve it moments later. A local edit would render a
     * state the server has already left -- and guessing `charged` here would be
     * a second copy of a rule that reads the charge ledger.
     */
    async rearmStrandedItem(item) {
      if (this.busyItemIds.includes(item.id)) return;
      this.busyItemIds = [...this.busyItemIds, item.id];
      try {
        await rearmReleaseItem(item.id);
        const batch = this.batches.find((b) => b.rawId === item.batchId)
          || this.batches.find((b) =>
            (b.strandedItems || []).some((i) => i.id === item.id));
        if (batch) {
          await this.loadStrandedItems(batch);
          await this.loadPreflight(batch);
        }
      } catch (err) {
        // Deliberately does NOT say the order is beyond help: a 409 here means
        // "not in a state we can revive" (already at the decorator, or another
        // release is working it), which is information, not a dead end.
        this.loadError =
          "Could not queue that order again. Try again in a moment.";
      } finally {
        this.busyItemIds = this.busyItemIds.filter((id) => id !== item.id);
      }
    },

    /**
     * Close a batch over its unresolved orders.
     *
     * DESTRUCTIVE, and the confirmation that guards it lives in `BatchRow` --
     * this method runs only after a human has accepted it, so it does not
     * ask again.
     *
     * IT DOES NOT REFUND, and this handler must never start. Jake,
     * 2026-09-15: an automatic refund is the trust-buster. What the response
     * carries instead is `refund_needed_orders`, the list of shoppers the
     * operator now owes money back -- which is why the batch is re-read
     * afterwards rather than simply removed from the screen.
     */
    async abandonBatch(batch) {
      if (this.abandoningBatchIds.includes(batch.id)) return;
      this.abandoningBatchIds = [...this.abandoningBatchIds, batch.id];
      try {
        const res = await abandonRelease(batch);
        if (res.batch) {
          this.replaceBatch(res.batch);
          // The batch is now released, and the orders it owes refunds on are
          // exactly what the operator must see next -- "We will list them
          // here so you do not lose track" (the confirm copy). Re-read them.
          if (this.expandedId === res.batch.id) {
            await this.loadStrandedItems(res.batch);
          }
        }
      } catch (err) {
        this.loadError =
          "Could not close that batch. Nothing was changed -- try again.";
      } finally {
        this.abandoningBatchIds = this.abandoningBatchIds.filter(
          (id) => id !== batch.id
        );
      }
    },

    async retryOpenRegistration(batch) {
      try {
        this.replaceBatch(await retryOpen(batch));
      } catch (err) {
        this.loadError =
          "Could not register that batch with the decorator network.";
      }
    },

    openEditor(order) {
      this.editorBatch = this.batches.find((b) =>
        (b.orders || []).some((o) => o.id === order.id)
      );
      this.editorOrder = order;
      this.editorOpen = true;
    },

    /**
     * Persist the editor's lines, then restate the order FROM THE SERVER.
     *
     * The returned order is the one that lands in the list -- NOT the `lines`
     * that were just sent. Those are the WIRE payload: `OrderEditor.save()`
     * narrows every line to SAVABLE_KEYS (id / styleId / color / size / qty /
     * logoId) because the PATCH endpoint refuses unknown keys by name,
     * all-or-nothing. `base` and `discount` are deliberately not among them,
     * so writing that array back into the rendered order dropped the unit
     * price and the frozen discount from every edited line -- and the batch
     * value, which `batchTotal` sums client-side from `l.base`, collapsed.
     * Jake saw $0.00 on a single-order batch (2026-09-23, production).
     *
     * The number was FABRICATED, not stale: `orders.cart_total` is re-summed
     * server-side on every edit (`batch_order_edit.refresh_order_subtotal`)
     * and the list's roll-up is computed live from it, so there was never a
     * stale total to refresh. Refetching the list would have hidden a surface
     * that was inventing a number.
     *
     * No extra round-trip either: `updateOrderLines` ALREADY returns the
     * PATCH response normalized (the endpoint answers with the order re-read
     * through `_orders_for_batches`, which serves `unit_price_cents` and
     * `free_item_discount_cents`). The panel was simply discarding it.
     *
     * Falls back to patching the sent lines only if the server returns
     * nothing usable. That keeps the shopper/held/payment fields the response
     * carries from being wiped by an empty object, and it is the pre-existing
     * behaviour for a response the panel cannot read -- wrong money on screen
     * is better than an order that vanishes from the list mid-edit.
     */
    async saveOrder(lines) {
      try {
        const saved = await updateOrderLines(this.editorOrder, lines);
        if (saved && saved.id) {
          this.replaceOrder(this.editorOrder.id, saved);
        } else {
          this.applyOrderLines(this.editorOrder.id, lines);
        }
        this.editorOpen = false;
      } catch (err) {
        this.loadError = "Could not save that order.";
      }
    },

    async confirmCancelOrder(order) {
      try {
        await cancelOrder(order);
        this.removeOrder(order.id);
        this.editorOpen = false;
      } catch (err) {
        this.loadError = "Could not cancel that order.";
      }
    },

    /**
     * The batches an order in `batch` may move to: the store's OTHER open or
     * holding batches, among those loaded. The server re-checks every rule
     * (same store, both editable, order movable) under row locks, so this is
     * the offer, not the guard.
     */
    moveDestinationsFor(batch) {
      if (!batch || !isEditable(batch.status)) return [];
      return this.batches.filter((b) => b.id !== batch.id && isEditable(b.status));
    },

    openMove(batch, order) {
      this.moveSource = batch;
      this.moveOrder = order;
      this.moveError = "";
      this.moveKey += 1;
      this.moveOpen = true;
    },

    /**
     * Both batches change, so the list is re-read rather than patched: the
     * server owns membership and both rollups. Then the DESTINATION is
     * expanded, where the moved orders now are.
     *
     * Errors stay in the dialog. A response from the server is a refusal,
     * and the move is all-or-nothing, so "nothing was moved" is true. No
     * response (a dropped connection) means the outcome is UNKNOWN, and the
     * copy says to refresh rather than claiming nothing changed.
     */
    async confirmMove({ orders, destination }) {
      if (this.moving) return;
      this.moving = true;
      this.moveError = "";
      try {
        await moveOrdersToBatch(this.moveSource, orders, destination);
      } catch (err) {
        const res = err && err.response;
        if (res) {
          const detail = String((res.data && res.data.error) || "").replace(/\.+$/, "");
          this.moveError = detail
            ? `Nothing was moved: ${detail}.`
            : "Nothing was moved. Try again in a moment.";
        } else {
          this.moveError =
            "We could not confirm the move. Refresh the page to see where the orders are.";
        }
        this.moving = false;
        return;
      }
      this.moving = false;
      this.moveOpen = false;
      this.filter = "all";
      this.query = "";
      await this.load();
      this.expandedId = destination.id;
    },

    quickCancel(order) {
      // The destructive dialog lives in the editor, so a Cancel from the row
      // opens the editor on that order rather than acting immediately. One
      // confirmation path, not two.
      this.openEditor(order);
    },

    async goToNextBatch() {
      this.resultOpen = false;
      if (!this.resultNextBatchId) return;
      this.filter = "all";
      this.query = "";
      // The destination batch was OPENED BY THE RELEASE, so it is not in
      // `this.batches` -- `replaceBatch` only maps over what was already
      // loaded. Without this reload the user is told their held-back orders
      // "moved into batch B-1047, which is open", clicks through, and lands on
      // a list with no such batch: a silent no-op on the exact recovery path
      // the design built. Reload first, then expand.
      if (!this.batches.some((b) => b.id === this.resultNextBatchId)) {
        await this.load();
      }
      this.expandedId = this.resultNextBatchId;
    },

    replaceBatch(updated) {
      if (!updated || !updated.id) return;
      this.batches = this.batches.map((b) => (b.id === updated.id ? updated : b));
    },

    /**
     * Swap one order for the server's version of it.
     *
     * Matched on the PRE-EDIT id, because the replacement carries its own and
     * the two are not guaranteed equal -- `normalizeOrder` derives the id
     * from `display_id || O-<pk>`, and matching on the new one would silently
     * fail to find the row it is meant to replace.
     */
    replaceOrder(orderId, order) {
      this.batches = this.batches.map((b) => ({
        ...b,
        orders: (b.orders || []).map((o) => (o.id === orderId ? order : o)),
      }));
    },

    /**
     * Patch an order's lines in place.
     *
     * The FALLBACK path only -- see `saveOrder`. Kept because a response the
     * panel cannot read should still leave the edit visible rather than
     * dropping the order, but note it writes whatever array it is handed: if
     * that is the narrowed wire payload, the rendered order loses its prices.
     */
    applyOrderLines(orderId, lines) {
      this.batches = this.batches.map((b) => ({
        ...b,
        orders: (b.orders || []).map((o) =>
          o.id === orderId ? { ...o, lines: lines.map((l) => ({ ...l })) } : o
        ),
      }));
    },

    removeOrder(orderId) {
      this.batches = this.batches.map((b) => ({
        ...b,
        orders: (b.orders || []).filter((o) => o.id !== orderId),
      }));
    },
  },
};
</script>

<style scoped>
.bm-panel {
  max-width: 1100px;
}

.bm-panel__head {
  margin-bottom: 16px;
}

.bm-panel__title {
  font-size: 20px;
  font-weight: 500;
  color: var(--tf-fg-1);
  margin: 0 0 6px;
}

.bm-panel__lede {
  font-size: 13.5px;
  color: var(--tf-fg-3);
  line-height: 1.6;
  max-width: 720px;
  margin: 0;
}

.bm-panel__lede b {
  font-weight: 500;
  color: var(--tf-success-dark);
}

.bm-filters {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.bm-filters__chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.bm-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  cursor: pointer;
  padding: 7px 14px;
  min-height: 44px;
  font-size: 13px;
  font-family: inherit;
  background: var(--tf-surface);
  color: var(--tf-fg-2);
  border: 1px solid var(--tf-border-strong);
}

.bm-filter--on {
  background: var(--tf-secondary);
  color: var(--tf-fg-on-secondary);
  border-color: var(--tf-secondary);
  font-weight: 500;
}

/* "Needs you" is the only chip that can be amber, and only when non-zero. */
.bm-filter--warn {
  color: var(--tf-chip-pending-fg);
  border-color: var(--tf-chip-pending-border);
}

.bm-filter__count {
  font-size: 11.5px;
  background: var(--tf-surface-alt);
  color: var(--tf-fg-3);
  border-radius: 9999px;
  padding: 1px 7px;
}

.bm-filter--on .bm-filter__count {
  background: rgba(255, 255, 255, 0.2);
  color: var(--tf-fg-on-secondary);
}

.bm-filter--warn .bm-filter__count {
  background: var(--tf-chip-pending-bg);
  color: var(--tf-chip-pending-fg);
}

.bm-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--tf-surface);
  border: 1px solid var(--tf-border-strong);
  border-radius: 9999px;
  padding: 0 14px;
  min-height: 44px;
  flex: 0 1 240px;
}

.bm-search__icon {
  font-size: 18px;
  color: var(--tf-fg-4);
}

.bm-search__input {
  border: none;
  outline: none;
  font-size: 13.5px;
  font-family: inherit;
  width: 100%;
  background: transparent;
  color: var(--tf-fg-1);
}

.bm-loading,
.bm-more {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 4px;
  font-size: 13px;
  color: var(--tf-fg-3);
}

.bm-loading__spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--tf-primary-soft);
  border-top-color: var(--tf-primary);
  display: inline-block;
  animation: bm-spin 800ms linear infinite;
}

@keyframes bm-spin {
  to {
    transform: rotate(360deg);
  }
}

.bm-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--tf-error-soft);
  border: 1px solid var(--tf-chip-error-border);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  color: var(--tf-chip-error-fg);
}

.bm-more__btn {
  width: 100%;
  margin-top: 4px;
}

.bm-end {
  text-align: center;
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
  padding: 16px 0;
  margin: 0;
}

.bm-btn {
  border-radius: 9999px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 9px 18px;
  font-size: 13.5px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn__icon {
  font-size: 16px;
}

/* Mobile: single column, search takes the full width. */
@media (max-width: 599px) {
  .bm-search {
    flex: 1 0 100%;
  }
}
</style>
